import { feedbackByQuestionId, questions } from "./funnelData";
import type { FunnelConceptKey } from "./funnelConcept";

const resultStep = questions.length + 1;
const emailStep = resultStep + 1;

export type FunnelRouteOptions = {
  includeEmailStep?: boolean;
};

const shouldIncludeEmailStep = (options?: FunnelRouteOptions): boolean => options?.includeEmailStep ?? true;

export const getPaymentStep = (includeEmailStep = true): number => resultStep + (includeEmailStep ? 2 : 1);

/** Hash route for each funnel screen (used in URL + PostHog `$current_url`). */
export const getHashForScreen = (
  step: number,
  pendingFeedbackQuestionId: string | null,
  options?: FunnelRouteOptions
): string => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  if (pendingFeedbackQuestionId) {
    return `#feedback-${pendingFeedbackQuestionId}`;
  }
  if (step === 0) return "#welcome";
  if (step <= questions.length) return `#q-${questions[step - 1]?.id ?? "welcome"}`;
  if (step === resultStep) return "#result";
  if (includeEmailStep && step === emailStep) return "#email";
  return "#payment";
};

export const parseHashToScreen = (
  hash: string,
  options?: FunnelRouteOptions
): { step: number; pendingFeedbackQuestionId: string | null } | null => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  const normalized = hash.trim().toLowerCase();
  if (!normalized || normalized === "#welcome") {
    return { step: 0, pendingFeedbackQuestionId: null };
  }
  if (normalized === "#result") {
    return { step: resultStep, pendingFeedbackQuestionId: null };
  }
  if (normalized === "#email") {
    return { step: includeEmailStep ? emailStep : getPaymentStep(false), pendingFeedbackQuestionId: null };
  }
  if (normalized === "#payment") {
    return { step: getPaymentStep(includeEmailStep), pendingFeedbackQuestionId: null };
  }
  if (normalized.startsWith("#q-")) {
    const questionId = normalized.slice(3);
    const index = questions.findIndex((question) => question.id.toLowerCase() === questionId);
    if (index !== -1) {
      return { step: index + 1, pendingFeedbackQuestionId: null };
    }
  }
  if (normalized.startsWith("#feedback-")) {
    const questionId = normalized.slice(10);
    const index = questions.findIndex((question) => question.id.toLowerCase() === questionId);
    if (index !== -1 && feedbackByQuestionId[questions[index].id]) {
      return { step: index + 1, pendingFeedbackQuestionId: questions[index].id };
    }
  }
  return null;
};

/** Monotonic-ish index for drop-off charts (question then optional feedback for same quiz step). */
export const getFunnelStepIndex = (step: number, pendingFeedbackQuestionId: string | null): number =>
  pendingFeedbackQuestionId ? step * 10 + 1 : step * 10;

export const getFunnelScreenName = (
  step: number,
  pendingFeedbackQuestionId: string | null,
  options?: FunnelRouteOptions
): string => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  if (pendingFeedbackQuestionId) {
    return `feedback_${pendingFeedbackQuestionId}`;
  }
  if (step === 0) return "welcome";
  if (step <= questions.length) return questions[step - 1]?.id ?? "question";
  if (step === resultStep) return "result";
  if (includeEmailStep && step === emailStep) return "email";
  return "payment";
};

export const buildFunnelPageviewPayload = (
  step: number,
  pendingFeedbackQuestionId: string | null,
  extras: { recommendedPlan: string; entryConceptKey: FunnelConceptKey; entryConceptLabel: string },
  options?: FunnelRouteOptions
): Record<string, string | number | boolean> => {
  const hash = getHashForScreen(step, pendingFeedbackQuestionId, options);
  const url = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}${window.location.search}${hash}`
    : hash;
  const screenId = hash.startsWith("#") ? hash.slice(1) : hash;
  return {
    $current_url: url,
    funnel_hash_route: hash,
    funnel_screen_id: screenId,
    funnel_screen_name: getFunnelScreenName(step, pendingFeedbackQuestionId, options),
    funnel_step: step,
    funnel_step_index: getFunnelStepIndex(step, pendingFeedbackQuestionId),
    funnel_on_feedback: Boolean(pendingFeedbackQuestionId),
    funnel_embedded_step: true,
    recommended_plan: extras.recommendedPlan,
    entry_concept: extras.entryConceptKey,
    entry_concept_label: extras.entryConceptLabel,
    product: "linkhiv",
    funnel: "linkhiv_webfunnel"
  };
};

export const buildCheckoutCompletePageviewPayload = (
  extras?: { entryConceptKey?: string; entryConceptLabel?: string },
  options?: FunnelRouteOptions
): Record<string, string | number | boolean> => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  const paymentStep = getPaymentStep(includeEmailStep);
  const hash = "#checkout_complete";
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}${window.location.search}${hash}`
      : hash;
  return {
    $current_url: url,
    funnel_hash_route: hash,
    funnel_screen_id: "checkout_complete",
    funnel_screen_name: "checkout_complete",
    funnel_step: paymentStep + 1,
    funnel_step_index: (paymentStep + 1) * 10,
    funnel_on_feedback: false,
    funnel_embedded_step: true,
    entry_concept: extras?.entryConceptKey ?? "organized-angle",
    entry_concept_label: extras?.entryConceptLabel ?? "Organized angle",
    product: "linkhiv",
    funnel: "linkhiv_webfunnel"
  };
};
